/* @flow */
import * as React from 'react';
import { FormattedNumber } from 'react-intl';

import Divider from 'components/Divider.jsx';
import Glyph from 'components/Glyph.jsx';
import Heading from 'components/Heading.jsx';
import LayerTrigger from 'components/LayerTrigger.jsx';
import Link from 'components/Link.jsx';
import MediaObject from 'components/MediaObject.jsx';
import ReadReceipt from 'chat/components/ReadReceipt.jsx';
import Row from 'components/Row.jsx';
import Text from 'components/Text.jsx';
import TextWithGlyph from 'components/TextWithGlyph.jsx';
import Tooltip from 'components/Tooltip.jsx';
import UserAvatar from 'users/components/UserAvatar.jsx';

import './styles/DeliveryModalSection.scss';

function getSubtext(status: string, count: number, isEstimate: boolean): * {
  const baseErrorCopy =
    count === 1 ? (
      `The message couldn’t be delivered to ${count} person.`
    ) : (
      <span>
        The message couldn’t be delivered to <FormattedNumber value={count} /> people.
      </span>
    );
  switch (status) {
    case 'read':
      return (
        <span>
          {count === 1 ? (
            `${count} person received and saw your message.`
          ) : (
            <span>
              <FormattedNumber value={count} /> people received and saw your message.
            </span>
          )}
        </span>
      );
    case 'delivered':
      return (
        <span>
          {count === 1 ? (
            `${count} person received your message.`
          ) : (
            <span>
              <FormattedNumber value={count} /> people received your message.
            </span>
          )}
        </span>
      );
    case 'delivery_error':
      return (
        <span>
          <span>
            {isEstimate && count > 0 ? (
              <span>
                {baseErrorCopy} A list of individual errors is only available for messages sent to
                fewer than 250 people
              </span>
            ) : (
              baseErrorCopy
            )}
          </span>
          <br />
        </span>
      );
    case 'unreachable':
      return (
        <span>
          {count === 1 ? (
            <span>
              The message was not sent to {count} person without valid contact information on
              Remind.
            </span>
          ) : (
            <span>
              The message was not sent to <FormattedNumber value={count} /> people without valid
              contact information on Remind.
            </span>
          )}
        </span>
      );
    default:
      return '';
  }
}

type Props = {
  title: string,
  count: number,
  status: string,
  users: Array<*>,
  percent?: number,
  isEstimate: boolean,
  onSectionFold?: Function,
  onSectionUnfold?: Function,
  onRequestCSV?: () => void,
  downloadAvailable?: ?boolean,
  downloadAvailableUntil?: ?Date,
};

type State = {
  folded: boolean,
};

export class DeliveryModalSection extends React.Component<Props, State> {
  state: State = {
    folded: true,
  };

  toggleFold = () => {
    if (this.state.folded) {
      this.setState({ folded: false }, () => {
        if (this.props.onSectionUnfold) {
          this.props.onSectionUnfold();
        }
      });
    } else {
      this.setState({ folded: true }, () => {
        if (this.props.onSectionFold) {
          this.props.onSectionFold();
        }
      });
    }
  };

  canUnfold(): boolean {
    return !this.props.isEstimate && this.props.users && this.props.users.length > 0;
  }

  foldIcon() {
    if (!this.props.users || this.props.users.length <= 0) {
      return null;
    }

    return this.state.folded ? (
      <Glyph solo size="small" color="brand" name="chevron-down" />
    ) : (
      <Glyph solo size="small" color="brand" name="chevron-up" />
    );
  }

  renderSectionHeader = () => {
    return (
      <div className="DeliveryModalSection-header">
        <div>
          <div>
            <Heading level="3">
              <MediaObject gutter="xsmall" center>
                <Text>{this.props.title}</Text>
                <Link data-test="fold-icon" onClick={this.toggleFold}>
                  {this.foldIcon()}
                </Link>
              </MediaObject>
            </Heading>
          </div>
          <div>
            <Text color="thunder" size="xxsmall">
              {getSubtext(this.props.status, this.props.count, this.props.isEstimate)}
            </Text>
          </div>
        </div>
        {!isNaN(this.props.percent) && (
          <div>
            <MediaObject gutter="xsmall" center>
              <ReadReceipt iconSize="large" glyphDisplay status={this.props.status} />
              <Text size="xlarge">{this.props.percent}%</Text>
            </MediaObject>
          </div>
        )}
      </div>
    );
  };

  renderDeliveryError = (deliveryError: *, name: string) => {
    const tooltipCopy = {
      undelivered: `Your message may have been blocked by a spam filter or ${name} may have turned off their phone. Please try again.`,
      failed: `Your message may have been blocked by a spam filter or ${name} may have turned off their phone. Please try again.`,
      blocked: `${name} needs to turn on push notifications, text messages, or emails for Remind.`,
      expired: `Your message may have been blocked by a spam filter or ${name} may have turned off their phone. Please try again.`,
      invalid: `${name} may have mistyped their email or changed their phone number. They need to update their contact information.`,
      unsupported: `${name} has a mobile carrier that doesn’t receive text messages from Remind. They need to download the Remind app.`,
    };
    return (
      <LayerTrigger
        noPointer
        layer={
          <Tooltip alignment="top" placement="left">
            {tooltipCopy[deliveryError.code] ||
              `Something went wrong in delivering your message to ${name}.`}
          </Tooltip>
        }>
        <div>
          <MediaObject gutter="xsmall" figure="right">
            <Text color="lava" size="xxsmall">
              {deliveryError.message}
            </Text>
            <Glyph color="lava" name="help" solo />
          </MediaObject>
        </div>
      </LayerTrigger>
    );
  };

  renderExpiredCSVDownload = () => {
    const { folded } = this.state;
    const expiredCSV = (
      <span data-test="expired-csv-download">
        <TextWithGlyph
          color="eclipse"
          glyphColor="eclipse"
          weight="semibold"
          glyph="spreadsheet"
          gutter="xsmall"
          size="xxsmall">
          Download list (CSV file) unavailable after 30 days
        </TextWithGlyph>
      </span>
    );
    return folded ? (
      expiredCSV
    ) : (
      <React.Fragment>
        <Row size="small">{expiredCSV}</Row>
        <Divider size="flush" />
      </React.Fragment>
    );
  };

  renderCSVDownload = () => {
    const { status } = this.props;
    const { folded } = this.state;
    const statusToUserTypes = {
      delivery_error: 'errored_users',
      unreachable: 'unreachable_users',
    };
    const csvLink = (
      <MediaObject figure="right" center data-test="csv-download">
        <Link
          eventKey={`delivery_summary.${statusToUserTypes[status]}.request_csv.click`}
          onClick={this.props.onRequestCSV}>
          <TextWithGlyph weight="semibold" glyph="spreadsheet" gutter="xsmall" size="xxsmall">
            Download list (CSV file)
          </TextWithGlyph>
        </Link>
        {this.props.downloadAvailableUntil ? (
          <Text
            size="xxsmall"
            color="thunder">{`Available until ${this.props.downloadAvailableUntil.toString()}`}</Text>
        ) : (
          <span />
        )}
      </MediaObject>
    );
    return folded ? (
      csvLink
    ) : (
      <React.Fragment>
        <Row size="small">{csvLink}</Row>
        <Divider size="flush" />
      </React.Fragment>
    );
  };

  renderDownload = () => {
    const { downloadAvailable } = this.props;
    if (downloadAvailable) {
      return this.renderCSVDownload();
    } else if (downloadAvailable === false) {
      return this.renderExpiredCSVDownload();
    }
    return null;
  };

  render() {
    const sectionHeaderWithCSV = (
      <React.Fragment>
        <Row size="xsmall">{this.renderSectionHeader()}</Row>
        <div data-test="download-container">{this.renderDownload()}</div>
      </React.Fragment>
    );
    return (
      <div className="DeliveryModalSection">
        {this.state.folded &&
          (this.props.isEstimate ? sectionHeaderWithCSV : this.renderSectionHeader())}
        {!this.state.folded && (
          <React.Fragment>
            {sectionHeaderWithCSV}
            {this.props.users.map(user => {
              return (
                <div key={user.uuid} className="DeliveryModalSection-user">
                  <MediaObject figure="right" center>
                    <MediaObject center gutter="small">
                      <UserAvatar size="small" user={user} />
                      <span>{user.name}</span>
                    </MediaObject>
                    {user.deliveryError ? (
                      this.renderDeliveryError(user.deliveryError, user.name)
                    ) : (
                      <span />
                    )}
                  </MediaObject>
                </div>
              );
            })}
          </React.Fragment>
        )}
      </div>
    );
  }
}

export default DeliveryModalSection;
